import { useState, useEffect } from 'react'
import { Modal } from '../ui/Modal'
import type { Gateway } from '../../types'

interface Props {
  open: boolean
  gateway: Gateway | null
  onClose: () => void
  onSave: (id: string, updates: Partial<Omit<Gateway, 'id' | 'site_id' | 'created_at'>>) => Promise<void>
}

export function EditGatewayDialog({ open, gateway, onClose, onSave }: Props) {
  const [name, setName] = useState('')
  const [dropletIp, setDropletIp] = useState('')
  const [go2rtcPort, setGo2rtcPort] = useState('1984')
  const [routerIp, setRouterIp] = useState('')
  const [tunnelIp, setTunnelIp] = useState('')
  const [carrier, setCarrier] = useState('')
  const [simOnly, setSimOnly] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (gateway) {
      setName(gateway.name)
      setDropletIp(gateway.droplet_ip)
      setGo2rtcPort(String(gateway.go2rtc_port))
      setRouterIp(gateway.router_ip ?? '')
      setTunnelIp(gateway.tunnel_ip ?? '')
      setCarrier(gateway.carrier ?? '')
      setSimOnly(gateway.sim_only)
      setError(null)
    }
  }, [gateway])

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!gateway) return

    setSaving(true)
    setError(null)
    try {
      await onSave(gateway.id, {
        name: name.trim(),
        droplet_ip: dropletIp.trim(),
        go2rtc_port: parseInt(go2rtcPort) || 1984,
        router_ip: routerIp.trim() || null,
        tunnel_ip: tunnelIp.trim() || null,
        carrier: carrier.trim() || null,
        sim_only: simOnly,
      })
      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save')
    } finally {
      setSaving(false)
    }
  }

  const inputCls = 'w-full bg-gray-50 border border-gray-200 rounded-md px-3 py-2 text-gray-900 text-sm placeholder-gray-400 focus:outline-none focus:border-brand focus:ring-1 focus:ring-brand/20'
  const labelCls = 'block text-xs uppercase tracking-wide text-gray-500 mb-1.5'

  return (
    <Modal open={open} onClose={onClose} title={`Edit — ${gateway?.name ?? 'Gateway'}`}>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className={labelCls}>Name</label>
          <input
            type="text"
            value={name}
            onChange={e => setName(e.target.value)}
            required
            className={inputCls}
            autoFocus
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className={labelCls}>Droplet IP</label>
            <input
              type="text"
              value={dropletIp}
              onChange={e => setDropletIp(e.target.value)}
              required
              className={inputCls}
            />
          </div>
          <div>
            <label className={labelCls}>Stream Port</label>
            <input
              type="number"
              value={go2rtcPort}
              onChange={e => setGo2rtcPort(e.target.value)}
              className={inputCls}
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className={labelCls}>Router IP</label>
            <input
              type="text"
              value={routerIp}
              onChange={e => setRouterIp(e.target.value)}
              placeholder="e.g. 192.168.1.1"
              className={inputCls}
            />
          </div>
          <div>
            <label className={labelCls}>Tunnel IP</label>
            <input
              type="text"
              value={tunnelIp}
              onChange={e => setTunnelIp(e.target.value)}
              placeholder="e.g. 10.8.0.2"
              className={inputCls}
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className={labelCls}>Carrier</label>
            <input
              type="text"
              value={carrier}
              onChange={e => setCarrier(e.target.value)}
              placeholder="e.g. EE, Three"
              className={inputCls}
            />
          </div>
          <div className="flex items-end pb-1">
            <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
              <input
                type="checkbox"
                checked={simOnly}
                onChange={e => setSimOnly(e.target.checked)}
                className="rounded border-gray-300"
              />
              SIM-only
            </label>
          </div>
        </div>

        {/* Signal strength (read-only) */}
        {gateway && (gateway.rssi !== null || gateway.rsrp !== null) && (
          <div className="bg-gray-50 rounded-md px-3 py-2">
            <p className="text-xs uppercase tracking-wide text-gray-500 mb-1">Signal</p>
            <div className="flex gap-4 text-xs text-gray-600 font-mono">
              {gateway.rssi !== null && <span>RSSI: {gateway.rssi} dBm</span>}
              {gateway.rsrp !== null && <span>RSRP: {gateway.rsrp} dBm</span>}
              {gateway.rsrq !== null && <span>RSRQ: {gateway.rsrq} dB</span>}
              {gateway.sinr !== null && <span>SINR: {gateway.sinr} dB</span>}
            </div>
          </div>
        )}

        {error && <p className="text-error text-sm">{error}</p>}

        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm text-gray-500 hover:text-gray-900 transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            className="bg-brand hover:bg-brand-dark text-white text-sm font-medium px-4 py-2 rounded-md transition-colors disabled:opacity-50"
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </form>
    </Modal>
  )
}
